import { useEffect, useRef, useState } from 'react';
import type { User } from 'firebase/auth';
import { CircleHelp, LogIn, Moon, Sun, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import logo from '../logo.png';

type DarkModeToggleProps = {
  isDark: boolean;
  onToggle: () => void;
};

function DarkModeToggle({ isDark, onToggle }: DarkModeToggleProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`inline-flex items-center rounded-full border px-2 py-2 text-sm font-semibold transition ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'}`}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? (
        <Sun className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Moon className="h-4 w-4" aria-hidden="true" />
      )}
    </button>
  );
}

type AppHeaderProps = {
  isDarkMode: boolean;
  onToggleTheme: () => void;
  currentUser: User | null;
  onSignIn: () => void;
  onCreateAccount: () => void;
  onHelp?: () => void;
  hideHelpButton?: boolean;
  hideCreateAccountButton?: boolean;
  onProfile?: () => void;
  onLogout?: () => void;
  showDashboardLink?: boolean;
};

function getUserDisplayName(user: User | null) {
  if (!user) {
    return null;
  }
  return user.displayName?.trim() || user.email || 'Account';
}

function getUserInitials(user: User | null) {
  if (!user) {
    return null;
  }
  const display = user.displayName?.trim() || '';
  const email = user.email ?? '';

  const prefixTokens = new Set(['mr', 'mr.', 'ms', 'ms.', 'mrs', 'mrs.', 'dr', 'dr.']);
  const suffixTokens = new Set(['jr', 'sr', 'iii', 'iv', 'phd', 'md', 'do', 'dvm', 'esq']);

  const normalizedParts = display
    .split(/\s+/)
    .filter(Boolean)
    .filter((part, index, arr) => {
      const value = part.replace(/[.,]/g, '').toLowerCase();
      if (index === 0 && prefixTokens.has(value)) {
        return false;
      }
      if (index === arr.length - 1 && suffixTokens.has(value)) {
        return false;
      }
      return true;
    });

  const parts = normalizedParts.length ? normalizedParts : (email ? email.split('@')[0].split(/[.\s_-]+/) : []);

  if (!parts.length) {
    return 'U';
  }

  const initials =
    parts.length === 1
      ? parts[0].slice(0, 2)
      : `${parts[0][0]}${parts[parts.length - 1][0]}`;
  return initials.toUpperCase();
}

export default function AppHeader({
  isDarkMode,
  onToggleTheme,
  currentUser,
  onSignIn,
  onCreateAccount,
  onHelp,
  hideHelpButton,
  hideCreateAccountButton,
  onProfile,
  onLogout,
  showDashboardLink,
}: AppHeaderProps) {
  const globalHeaderOuterClass = `w-full border-b ${isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-black/20 bg-white text-slate-900'}`;
  const globalHeaderInnerClass = 'mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8';
  const headerTitleClass = `text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`;
  const headerSubtitleClass = `text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`;
  const headerActionGroupClass = 'flex flex-wrap items-center justify-end gap-3';
  const headerSecondaryButtonClass = `inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${isDarkMode ? 'border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'}`;
  const headerPrimaryButtonClass = `inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold shadow-sm transition ${isDarkMode ? 'border-blue-500 bg-blue-500 text-white hover:bg-blue-600' : 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'}`;
  const userBadgeWrapperClass = `relative flex items-center gap-3 p-0 text-sm font-medium ${isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-600'}`;
  const userInitialsClass = `flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${isDarkMode ? 'bg-blue-500 text-white focus:ring-offset-slate-800' : 'bg-blue-600 text-white focus:ring-offset-white'}`;
  const dropdownMenuClass = `absolute right-0 top-full z-20 mt-2 w-40 overflow-hidden rounded-lg border text-sm shadow-lg ${isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-700'}`;
  const dropdownItemClass = `block w-full px-4 py-2 text-left transition ${isDarkMode ? 'text-slate-100 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`;
  const dropdownSeparatorClass = `h-px ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`;
  const userDisplayName = getUserDisplayName(currentUser);
  const userInitials = getUserInitials(currentUser);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, []);

  const handleMenuToggle = () => {
    setIsMenuOpen(prev => !prev);
  };

  const handleProfileClick = () => {
    setIsMenuOpen(false);
    onProfile?.();
  };

  const handleDashboardClick = () => {
    setIsMenuOpen(false);
    navigate('/dashboard');
  };

  const handleLogoutClick = () => {
    setIsMenuOpen(false);
    onLogout?.();
  };

  return (
    <header className={globalHeaderOuterClass}>
      <div className={globalHeaderInnerClass}>
        <div className="flex items-center gap-3">
          <img src={logo} alt="Happy Tail Tracker logo" className="h-12 w-12 rounded-xl object-contain" />
          <div>
            <p className={headerTitleClass}>Happy Tail Tracker</p>
            <p className={headerSubtitleClass}>Made with love for the pets you love.</p>
          </div>
        </div>
        <div className={headerActionGroupClass}>
          <DarkModeToggle isDark={isDarkMode} onToggle={onToggleTheme} />
          {currentUser && userDisplayName && userInitials ? (
            <div ref={userMenuRef} className={userBadgeWrapperClass}>
              <span className={isDarkMode ? 'text-slate-100' : 'text-slate-700'}>
                {userDisplayName}
              </span>
              <button
                type="button"
                onClick={handleMenuToggle}
                className={userInitialsClass}
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
              >
                {userInitials}
              </button>
              {isMenuOpen ? (
                <div className={dropdownMenuClass} role="menu" aria-label="Account options">
                  {showDashboardLink ? (
                    <button
                      type="button"
                      className={dropdownItemClass}
                      onClick={handleDashboardClick}
                      role="menuitem"
                    >
                      Dashboard
                    </button>
                  ) : null}
                  {showDashboardLink ? <div className={dropdownSeparatorClass} role="separator" /> : null}
                  <button type="button" className={dropdownItemClass} onClick={handleProfileClick} role="menuitem">
                    Profile
                  </button>
                  <div className={dropdownSeparatorClass} role="separator" />
                  <button type="button" className={dropdownItemClass} onClick={handleLogoutClick} role="menuitem">
                    Logout
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              {!hideHelpButton ? (
                <button type="button" className={headerSecondaryButtonClass} onClick={onHelp}>
                  <CircleHelp className="h-4 w-4" aria-hidden="true" />
                  What is this?
                </button>
              ) : null}
              <button type="button" className={headerSecondaryButtonClass} onClick={onSignIn}>
                <LogIn className="h-4 w-4" aria-hidden="true" />
                Sign In
              </button>
              {!hideCreateAccountButton ? (
                <button type="button" className={headerPrimaryButtonClass} onClick={onCreateAccount}>
                  <UserPlus className="h-4 w-4" aria-hidden="true" />
                  Create Account
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
    </header>
  );
}
